import React, { FC, useEffect, useMemo } from 'react';
import { useAction } from '@reatom/npm-react';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useServices, t, WeeklyRewards, Routes, useModal } from '@whitesharx/starfall-frontend-core';
import { Lessons, ExerciseData, useLessonDailyLimit } from '@whitesharx/starfall-frontend-core/learn_screen';
import '@whitesharx/starfall-frontend-core/learn_screen.css';

import UserInfo from '~/src/components/UserInfo';
import InfoHeader from '~/src/components/InfoHeader';
import PageLayoutHeader from '~/src/components/PageLayoutHeader';
import ChessRoyaleBanner from '~/src/components/ChessRoyaleBanner';
import CommonCustomApp from '~/src/components/CustomApp/CommonCustomApp';
import LessonsSkeletonPage from '~/src/skeletons/learn/Lessons';
import VIPModal from '~/src/components/VIPModal';

import { AppComponentType, AppTypes } from '../../_app';
import { parseQueryParams } from '~/src/utils/parseQueryParams';

import { bannerContainerStyles } from '~/styles/pages/common.css';
import { updateRightContainerAction } from '~/src/models/layout';

export const CATEGORY_ID_KEY = 'categoryId';
export const LESSON_ID_KEY = 'lessonId';

const LessonsRealPage: FC = () => {
  const router = useRouter();
  const { useResponsive, Adsense } = useServices();
  const updateRightContainer = useAction(updateRightContainerAction);
  const { isDesktop } = useResponsive();
  const { canStartLesson, isLoading } = useLessonDailyLimit();
  const { isOpen: isOpenVIPModal, open: openVIPModal, close: closeVIPModal } = useModal();

  const categoryId = parseQueryParams(router.asPath, CATEGORY_ID_KEY);

  const onChangeCategory = (category: string) => {
    router.replace({ query: { [CATEGORY_ID_KEY]: category } });
  };

  const onStartLesson = (lesson: ExerciseData) => {
    router.push({
      pathname: Routes.lessonsTheory,
      query: { [CATEGORY_ID_KEY]: categoryId, [LESSON_ID_KEY]: lesson.id },
    });
  };

  const rightContainer = useMemo(() => {
    if (isDesktop) {
      return (
        <>
          <UserInfo />
          {Adsense.renderBanner({
            placement: 'lessons_screen_banner_right',
            format: 'rectangle',
            slot: 'bannerRight',
            withSafeMargins: true,
          })}
          <div className={bannerContainerStyles}>
            <ChessRoyaleBanner />
          </div>
        </>
      );
    }
  }, [isDesktop]);

  useEffect(() => {
    updateRightContainer(rightContainer);
  }, [rightContainer]);

  return (
    <>
      <Head>
        <title>{t('browser-title.lessons')}</title>
      </Head>
      <>
        {!isDesktop && <InfoHeader />}
        <PageLayoutHeader title={t('learn.lessons.page-title')} name="lesson" />
        <Lessons
          canStartLesson={canStartLesson}
          categoryId={categoryId}
          onChangeCategory={onChangeCategory}
          onStartLesson={onStartLesson}
          isLoadingLimit={isLoading}
          onGetVip={openVIPModal}
        />
        {isOpenVIPModal && <VIPModal onClose={closeVIPModal} />}
        <WeeklyRewards />
      </>
    </>
  );
};

const LessonsPage: FC<AppTypes> & AppComponentType = (props) =>
  props.skeleton ? <LessonsSkeletonPage /> : <LessonsRealPage {...props} />;

LessonsPage.render = (props) => <CommonCustomApp {...props} />;

export default LessonsPage;
